/**
 * ThinkingPanel - Collapsible panel showing agent thinking process
 * 
 * Displays:
 * - Active agents with icons
 * - Timeline of thinking steps with agent icons
 * - Auto-opens during streaming
 * - Auto-closes after completion
 * - Duration timer
 * - Auto-scroll during streaming
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Lightbulb01, ChevronRight, CheckCircle, SearchLg, Settings01, ArrowRight, Package } from "@untitledui/icons";
import { useTranslation } from "react-i18next";
import { AgentIcon } from "./agent-icon";
import type { ThinkingStep } from "@/hooks/use-chat-stream";
import { cx } from "@/utils/cx";

const getStepIcon = (stepType: string) => {
    switch (stepType) {
        case "thinking":
            return Lightbulb01;
        case "tool_call":
            return Settings01;
        case "rag_search":
            return SearchLg;
        case "delegate":
            return ArrowRight;
        case "response":
            return CheckCircle;
        case "compaction":
            return Package;
        default:
            return Lightbulb01;
    }
};

const getStepColor = (stepType: string) => {
    switch (stepType) {
        case "thinking":
            return "text-blue-500";
        case "tool_call":
            return "text-purple-500";
        case "rag_search":
            return "text-green-500";
        case "delegate":
            return "text-orange-500";
        case "response":
            return "text-brand";
        case "compaction":
            return "text-gray-500 dark:text-gray-400";
        default:
            return "text-tertiary";
    }
};

function getStepDuration(current: ThinkingStep, next?: ThinkingStep): string | null {
    if (!next?.timestamp || !current.timestamp) return null;
    const ms = new Date(next.timestamp).getTime() - new Date(current.timestamp).getTime();
    if (ms < 0) return null;
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
}

/** Step is a DB query (text_to_sql): never show raw JSON (SQL, tables, results) for security. */
function isDbQueryStep(step: ThinkingStep): boolean {
    if (step.step_type !== "tool_call" || !step.metadata) return false;
    if (step.metadata.name === "text_to_sql") return true;
    const out = step.metadata.output;
    if (out && typeof out === "object" && ("sql" in out || "tables_used" in out || "results" in out)) return true;
    return false;
}

/** If content looks like raw JSON with SQL/tool_result, return safe placeholder. */
function safeStepContent(content: string | undefined): string {
    if (!content) return "";
    if (/"sql"\s*:/i.test(content) || /"tool_result"/i.test(content) || /"tables_used"\s*:/i.test(content)) {
        return "Query eseguita sul database.";
    }
    return content;
}

export interface ThinkingPanelProps {
    /**
     * Thinking steps from the current message
     */
    thinkingSteps: ThinkingStep[];
    
    /**
     * Whether thinking is currently streaming
     */
    isStreaming: boolean;
    
    /**
     * Whether panel should be open by default
     */
    defaultOpen?: boolean;
    
    /**
     * Additional CSS classes
     */
    className?: string;
}

export function ThinkingPanel({
    thinkingSteps,
    isStreaming,
    defaultOpen = true,
    className,
}: ThinkingPanelProps) {
    const { t } = useTranslation(['common']);
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [duration, setDuration] = useState(0);
    const startTimeRef = useRef<number | null>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    
    // Get unique active agents from thinking steps
    const activeAgents = useMemo(() => {
        const agents = new Set<string>();
        thinkingSteps.forEach(step => {
            if (step.agent_name) {
                agents.add(step.agent_name);
            }
        });
        return Array.from(agents);
    }, [thinkingSteps]);
    
    // Timer per tracciare la durata del thinking
    useEffect(() => {
        if (isStreaming && !startTimeRef.current) {
            startTimeRef.current = Date.now();
        }
        
        if (isStreaming) {
            const interval = setInterval(() => {
                if (startTimeRef.current) {
                    setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
                }
            }, 1000);
            return () => clearInterval(interval);
        } else if (startTimeRef.current) {
            // Calcola durata finale
            setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }
    }, [isStreaming]);
    
    // Auto-apertura durante streaming
    useEffect(() => {
        if (isStreaming && !isOpen) {
            setIsOpen(true);
        }
    }, [isStreaming, isOpen]);
    
    // Auto-scroll rimosso - il contenuto della chat scrolla invece
    
    // Auto-chiudi quando lo streaming termina con delay minimo per permettere la lettura
    useEffect(() => {
        if (!isStreaming && thinkingSteps.length > 0) {
            // Calcola delay minimo basato sul contenuto per permettere la lettura
            // Delay minimo: 3 secondi
            // Aggiungi tempo extra basato sul numero di caratteri (circa 200ms per ogni 100 caratteri)
            const totalContentLength = thinkingSteps.reduce((sum, step) => sum + (step.content?.length || 0), 0);
            const readingTime = Math.max(3000, 3000 + (totalContentLength / 100) * 200);
            // Aggiungi anche tempo basato sul numero di step (100ms per step)
            const stepsTime = thinkingSteps.length * 100;
            const totalDelay = Math.max(3000, readingTime + stepsTime);
            
            const timer = setTimeout(() => setIsOpen(false), totalDelay);
            return () => clearTimeout(timer);
        }
    }, [isStreaming, thinkingSteps]);
    
    // Formatta la durata
    const formatDuration = (seconds: number) => {
        if (seconds < 60) {
            return `${seconds} ${seconds === 1 ? 'secondo' : 'secondi'}`;
        }
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
    };
    
    // Non mostrare se non ci sono thinking steps
    if (thinkingSteps.length === 0 && !isStreaming) {
        return null;
    }
    
    return (
        <div className={cx("mb-3 rounded-lg border border-purple-200/80 dark:border-purple-800/80 overflow-hidden", className)}>
            {/* Header cliccabile - compatto */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center gap-2 px-3 py-2 
                           bg-purple-50/80 dark:bg-purple-900/15 
                           hover:bg-purple-100/80 dark:hover:bg-purple-900/25 
                           transition-colors"
            >
                {/* Icona collapse */}
                <motion.span
                    animate={{ rotate: isOpen ? 90 : 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <ChevronRight className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                </motion.span>

                {/* Icona cervello con animazione durante streaming */}
                <Lightbulb01 
                    className={cx(
                        "w-4 h-4 text-purple-500",
                        isStreaming && "animate-pulse"
                    )} 
                />

                {/* Titolo + durata in una riga */}
                <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                    {isStreaming 
                        ? t('aiChat.reasoning.thinkingInProgress', { defaultValue: "Ragionamento in corso" })
                        : t('aiChat.reasoning.thinking', { defaultValue: "Ragionamento" })
                    }
                </span>
                {duration > 0 && (
                    <span className="ml-auto text-xs text-purple-500 tabular-nums">
                        {formatDuration(duration)}
                    </span>
                )}
                {isStreaming && (
                    <span className="flex gap-0.5" aria-hidden>
                        <span className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                )}
            </button>

            {/* Contenuto collassabile */}
            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                        <div
                            ref={contentRef}
                            className="px-3 py-2 bg-purple-50/40 dark:bg-purple-900/10"
                        >
                            {/* Active Agents */}
                            {activeAgents.length > 0 && (
                                <div className="mb-4 space-y-2">
                                    <div className="text-xs font-medium text-tertiary mb-2">
                                        {t('aiChat.reasoning.activeAgents', { defaultValue: "Agenti Attivi" })}
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {activeAgents.map((agentName) => (
                                            <AgentIcon
                                                key={agentName}
                                                agentName={agentName}
                                                size="sm"
                                                showLabel={true}
                                                showBadge={isStreaming}
                                                badgeColor="purple"
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Thinking Steps Timeline */}
                            {thinkingSteps.length > 0 && (
                                <div className="space-y-1.5">
                                    <div className="text-xs font-medium text-tertiary mb-1.5">
                                        {t('aiChat.reasoning.steps', { defaultValue: "Passaggi di ragionamento" })}
                                    </div>
                                    {thinkingSteps.map((step, index) => {
                                        const isLastStep = index === thinkingSteps.length - 1;
                                        const isStreamingStep = isStreaming && isLastStep;
                                        const StepIcon = getStepIcon(step.step_type);
                                        const stepColorClass = getStepColor(step.step_type);
                                        const durationStr = getStepDuration(step, thinkingSteps[index + 1]);
                                        const hasToolCallDetails =
                                            step.step_type === "tool_call" &&
                                            !isDbQueryStep(step) &&
                                            step.metadata &&
                                            (step.metadata.input != null || step.metadata.output != null);

                                        return (
                                            <motion.div
                                                key={step.id}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.2, delay: index * 0.05 }}
                                                className="flex items-start gap-3 p-2 rounded-md bg-white/60 dark:bg-gray-800/50 border border-purple-100/80 dark:border-purple-800/50"
                                            >
                                                {/* Agent Icon */}
                                                <div className="flex-shrink-0 mt-0.5">
                                                    <AgentIcon agentName={step.agent_name} size="sm" />
                                                </div>

                                                {/* Step Type Icon */}
                                                <StepIcon className={cx("size-4 mt-0.5 flex-shrink-0", stepColorClass)} />

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                        <span className="text-xs font-medium text-primary">
                                                            {step.agent_name}
                                                        </span>
                                                        <span className="text-xs text-tertiary">
                                                            {new Date(step.timestamp).toLocaleTimeString()}
                                                        </span>
                                                        {durationStr != null && (
                                                            <span className="text-xs text-tertiary">
                                                                ({durationStr})
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-primary">
                                                        {safeStepContent(step.content)}
                                                        {isStreamingStep && (
                                                            <span className="inline-block w-2 h-4 bg-purple-500 animate-pulse ml-1" />
                                                        )}
                                                    </div>
                                                    {hasToolCallDetails && (
                                                        <details className="mt-1 text-xs text-tertiary group/details">
                                                            <summary className="cursor-pointer hover:text-primary select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-1 rounded px-1 -mx-1 inline-flex items-center gap-1">
                                                                <span className="inline-block transition-transform group-open/details:rotate-90" aria-hidden>▶</span>
                                                                {t('aiChat.reasoning.showDetails', { defaultValue: "Dettagli" })}
                                                            </summary>
                                                            {step.metadata?.input != null && (
                                                                <pre className="mt-1 p-2 rounded bg-gray-100 dark:bg-gray-800 overflow-x-auto text-xs max-h-32 overflow-y-auto">
                                                                    {JSON.stringify(step.metadata.input, null, 2)}
                                                                </pre>
                                                            )}
                                                            {step.metadata?.output != null && (
                                                                <pre className="mt-1 p-2 rounded bg-gray-100 dark:bg-gray-800 overflow-x-auto text-xs max-h-32 overflow-y-auto">
                                                                    {JSON.stringify(step.metadata.output, null, 2)}
                                                                </pre>
                                                            )}
                                                        </details>
                                                    )}
                                                    {step.metadata &&
                                                        Object.keys(step.metadata).length > 0 &&
                                                        step.step_type !== "tool_call" && (
                                                            <div className="mt-1 text-xs text-tertiary">
                                                                {JSON.stringify(step.metadata, null, 2)}
                                                            </div>
                                                        )}
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}
                            
                            {/* Stato iniziale: analisi in corso (prima che arrivi il primo step) */}
                            {thinkingSteps.length === 0 && isStreaming && (
                                <div className="flex items-center gap-3 p-2 rounded-md bg-white/60 dark:bg-gray-800/50 border border-purple-100/80 dark:border-purple-800/50">
                                    <Lightbulb01 className="size-4 flex-shrink-0 text-blue-500 animate-pulse" />
                                    <span className="text-sm text-primary">
                                        {t('aiChat.reasoning.analyzingRequest', { defaultValue: "Analisi della richiesta in corso..." })}
                                    </span>
                                    <span className="flex gap-0.5 ml-auto" aria-hidden>
                                        <span className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                        <span className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                        <span className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                    </span>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

